import { create } from 'zustand';
import { api } from './api';

const useCrmStore = create((set, get) => ({
  // State
  stages: [],
  leads: [],
  selectedLead: null,
  loading: false,
  error: null,
  filters: {},

  // Pagination State - por stage
  pagination: {}, // { [stageId]: { page: 1, perPage: 100, total: 0, pages: 0 } }
  leadsByStage: {}, // { [stageId]: [leads] }
  stageLoading: {}, // { [stageId]: boolean }

  // Sales State
  sales: [],
  transactions: [],
  salesMetrics: null,
  revenueEvolution: [],
  financialSummary: null,
  salesLoading: false,

  // Stage Actions
  fetchStages: async () => {
    set({ loading: true, error: null });
    try {
      const response = await api.get('/api/crm/stages');
      set({ stages: response.data, loading: false });
    } catch (error) {
      set({
        error: error.response?.data?.error || 'Failed to fetch stages',
        loading: false
      });
    }
  },

  createStage: async (stageData) => {
    set({ loading: true, error: null });
    try {
      const response = await api.post('/api/crm/stages', stageData);
      const newStage = response.data;

      set(state => ({
        stages: [...state.stages, newStage].sort((a, b) => a.order - b.order),
        // Initialize empty data for the new stage to prevent inconsistency
        leadsByStage: {
          ...state.leadsByStage,
          [newStage.id]: []
        },
        pagination: {
          ...state.pagination,
          [newStage.id]: { page: 1, perPage: 100, total: 0, pages: 0 }
        },
        stageLoading: {
          ...state.stageLoading,
          [newStage.id]: false
        },
        loading: false
      }));

      return newStage;
    } catch (error) {
      set({
        error: error.response?.data?.error || 'Failed to create stage',
        loading: false
      });
      throw error;
    }
  },

  updateStage: async (stageId, updates) => {
    try {
      const response = await api.put(`/api/crm/stages/${stageId}`, updates);
      const updatedStage = response.data;

      set(state => ({
        stages: state.stages.map(stage =>
          stage.id === stageId ? updatedStage : stage
        ).sort((a, b) => a.order - b.order)
      }));

      return updatedStage;
    } catch (error) {
      set({ error: error.response?.data?.error || 'Failed to update stage' });
      throw error;
    }
  },

  deleteStage: async (stageId) => {
    try {
      await api.delete(`/api/crm/stages/${stageId}`);

      set(state => {
        // Clean up orphan data for the deleted stage
        const { [stageId]: removedLeads, ...remainingLeadsByStage } = state.leadsByStage;
        const { [stageId]: removedPagination, ...remainingPagination } = state.pagination;
        const { [stageId]: removedLoading, ...remainingStageLoading } = state.stageLoading;

        return {
          stages: state.stages.filter(stage => stage.id !== stageId),
          leadsByStage: remainingLeadsByStage,
          pagination: remainingPagination,
          stageLoading: remainingStageLoading
        };
      });
    } catch (error) {
      set({ error: error.response?.data?.error || 'Failed to delete stage' });
      throw error;
    }
  },

  reorderStages: async (stageOrders) => {
    const originalStages = get().stages;

    // Optimistic update
    set(state => ({
      stages: state.stages.map(stage => {
        const newOrder = stageOrders.find(so => so.id === stage.id);
        return newOrder ? { ...stage, order: newOrder.order } : stage;
      }).sort((a, b) => a.order - b.order)
    }));

    try {
      const response = await api.post('/api/crm/stages/reorder', {
        stage_orders: stageOrders
      });
      set({ stages: response.data });
      return response.data;
    } catch (error) {
      // Rollback on error
      set({ stages: originalStages });
      set({ error: error.response?.data?.error || 'Failed to reorder stages' });
      throw error;
    }
  },

  // Lead Actions
  fetchLeads: async (filters = {}) => {
    set({ loading: true, error: null, filters });
    try {
      // Build query params properly for arrays
      const params = new URLSearchParams();

      // Handle multi-select filters
      if (filters.stage_ids && filters.stage_ids.length > 0) {
        filters.stage_ids.forEach(id => params.append('stage_id', id));
      }
      if (filters.sources && filters.sources.length > 0) {
        filters.sources.forEach(s => params.append('source', s));
      }
      if (filters.priorities && filters.priorities.length > 0) {
        filters.priorities.forEach(p => params.append('priority', p));
      }
      if (filters.statuses && filters.statuses.length > 0) {
        filters.statuses.forEach(s => params.append('status', s));
      }
      if (filters.tags && filters.tags.length > 0) {
        filters.tags.forEach(t => params.append('tag', t));
      }

      // Handle single-value filters
      if (filters.search) params.append('search', filters.search);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);

      // Handle pagination
      if (filters.page) params.append('page', filters.page);
      if (filters.per_page) params.append('per_page', filters.per_page);

      const response = await api.get(`/api/crm/leads?${params}`);
      set({ leads: response.data.leads, loading: false });
    } catch (error) {
      set({
        error: error.response?.data?.error || 'Failed to fetch leads',
        loading: false
      });
    }
  },

  // Fetch leads for a specific stage with pagination
  fetchLeadsByStage: async (stageId, page = 1, perPage = 100, filters = {}) => {
    set(state => ({
      stageLoading: { ...state.stageLoading, [stageId]: true }
    }));

    try {
      const params = new URLSearchParams();

      // Stage filter
      params.append('stage_id', stageId);

      // Pagination
      params.append('page', page);
      params.append('per_page', perPage);

      // Apply other filters
      if (filters.sources && filters.sources.length > 0) {
        filters.sources.forEach(s => params.append('source', s));
      }
      if (filters.priorities && filters.priorities.length > 0) {
        filters.priorities.forEach(p => params.append('priority', p));
      }
      if (filters.statuses && filters.statuses.length > 0) {
        filters.statuses.forEach(s => params.append('status', s));
      }
      if (filters.tags && filters.tags.length > 0) {
        filters.tags.forEach(t => params.append('tag', t));
      }
      if (filters.search) params.append('search', filters.search);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);

      const response = await api.get(`/api/crm/leads?${params}`);
      const { leads, total, pages } = response.data;

      set(state => ({
        leadsByStage: {
          ...state.leadsByStage,
          [stageId]: leads
        },
        pagination: {
          ...state.pagination,
          [stageId]: { page, perPage, total, pages }
        },
        stageLoading: { ...state.stageLoading, [stageId]: false }
      }));

      return { leads, total, pages, page };
    } catch (error) {
      set(state => ({
        stageLoading: { ...state.stageLoading, [stageId]: false },
        error: error.response?.data?.error || 'Failed to fetch leads for stage'
      }));
      throw error;
    }
  },

  // Fetch all stages with their leads (initial load)
  fetchAllStagesWithLeads: async (perPage = 100, filters = {}) => {
    const stages = get().stages;
    if (stages.length === 0) return;

    set({ loading: true });

    try {
      // Fetch leads for all stages in parallel
      await Promise.all(
        stages.map(stage => get().fetchLeadsByStage(stage.id, 1, perPage, filters))
      );
      set({ loading: false });
    } catch (error) {
      set({ loading: false });
    }
  },

  // Change page for a specific stage
  changePageForStage: async (stageId, newPage, filters = {}) => {
    const pagination = get().pagination[stageId];
    const perPage = pagination?.perPage || 100;
    return get().fetchLeadsByStage(stageId, newPage, perPage, filters);
  },

  createLead: async (leadData) => {
    set({ loading: true, error: null });
    try {
      const response = await api.post('/api/crm/leads', leadData);
      const newLead = response.data;

      // Add to local state
      set(state => ({
        leads: [newLead, ...state.leads],
        loading: false
      }));

      return newLead;
    } catch (error) {
      set({
        error: error.response?.data?.error || 'Failed to create lead',
        loading: false
      });
      throw error;
    }
  },

  updateLead: async (leadId, updates) => {
    try {
      const response = await api.put(`/api/crm/leads/${leadId}`, updates);
      const updatedLead = response.data;

      // Update local state
      set(state => ({
        leads: state.leads.map(lead =>
          lead.id === leadId ? updatedLead : lead
        )
      }));

      return updatedLead;
    } catch (error) {
      set({ error: error.response?.data?.error || 'Failed to update lead' });
      throw error;
    }
  },

  moveLeadToStage: async (leadId, newStageId) => {
    const originalLeads = get().leads;

    // Optimistic update
    set(state => ({
      leads: state.leads.map(lead =>
        lead.id === leadId ? { ...lead, stage_id: newStageId } : lead
      )
    }));

    try {
      const response = await api.put(`/api/crm/leads/${leadId}/stage`, {
        stage_id: newStageId
      });
      return response.data;
    } catch (error) {
      // Rollback on error
      set({ leads: originalLeads });
      set({ error: error.response?.data?.error || 'Failed to move lead' });
      throw error;
    }
  },

  selectLead: (lead) => set({ selectedLead: lead }),

  // Deletar lead e atualizar estado local (evita window.location.reload)
  deleteLead: async (leadId) => {
    try {
      await api.delete(`/api/crm/leads/${leadId}`);

      // Encontrar o stage do lead para atualizar contadores
      const allLeads = Object.values(get().leadsByStage).flat();
      const deletedLead = allLeads.find(l => l.id === leadId);
      const stageId = deletedLead?.stage_id;

      // Remover do estado local
      set(state => {
        // Remover da lista geral de leads
        const newLeads = state.leads.filter(lead => lead.id !== leadId);

        // Remover do leadsByStage
        const newLeadsByStage = { ...state.leadsByStage };
        if (stageId && newLeadsByStage[stageId]) {
          newLeadsByStage[stageId] = newLeadsByStage[stageId].filter(
            lead => lead.id !== leadId
          );
        }

        // Atualizar contagem de paginação
        const newPagination = { ...state.pagination };
        if (stageId && newPagination[stageId]) {
          newPagination[stageId] = {
            ...newPagination[stageId],
            total: Math.max(0, (newPagination[stageId].total || 1) - 1)
          };
        }

        return {
          leads: newLeads,
          leadsByStage: newLeadsByStage,
          pagination: newPagination,
          selectedLead: state.selectedLead?.id === leadId ? null : state.selectedLead
        };
      });

      return true;
    } catch (error) {
      set({ error: error.response?.data?.error || 'Failed to delete lead' });
      throw error;
    }
  },

  fetchLeadDetails: async (leadId) => {
    set({ loading: true, error: null });
    try {
      const response = await api.get(`/api/crm/leads/${leadId}`);
      set({ selectedLead: response.data, loading: false });
    } catch (error) {
      set({
        error: error.response?.data?.error || 'Failed to fetch lead details',
        loading: false
      });
    }
  },

  exportLeads: async (filters) => {
    try {
      // Build query params
      const params = new URLSearchParams();

      if (filters.stage_ids && filters.stage_ids.length > 0) {
        filters.stage_ids.forEach(id => params.append('stage_id', id));
      }
      if (filters.sources && filters.sources.length > 0) {
        filters.sources.forEach(s => params.append('source', s));
      }
      if (filters.date_from) {
        params.append('date_from', filters.date_from);
      }
      if (filters.date_to) {
        params.append('date_to', filters.date_to);
      }
      if (filters.search) {
        params.append('search', filters.search);
      }

      const response = await api.get(`/api/crm/leads/export?${params}`, {
        responseType: 'blob'
      });

      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.message || 'Failed to export leads' });
      throw error;
    }
  },

  clearError: () => set({ error: null }),

  // ========== SALES ACTIONS ==========

  // Fetch sales for a lead
  fetchLeadSales: async (leadId) => {
    set({ salesLoading: true });
    try {
      const response = await api.get(`/api/crm/leads/${leadId}/sales`);
      set({ sales: response.data, salesLoading: false });
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.error || 'Failed to fetch sales', salesLoading: false });
      throw error;
    }
  },

  // Create a new sale
  createSale: async (leadId, saleData) => {
    set({ salesLoading: true });
    try {
      const response = await api.post(`/api/crm/leads/${leadId}/sales`, saleData);
      const newSale = response.data;

      set(state => ({
        sales: [newSale, ...state.sales],
        salesLoading: false
      }));

      return newSale;
    } catch (error) {
      set({ error: error.response?.data?.error || 'Failed to create sale', salesLoading: false });
      throw error;
    }
  },

  // Update a sale
  updateSale: async (saleId, updates) => {
    try {
      const response = await api.put(`/api/crm/sales/${saleId}`, updates);
      const updatedSale = response.data;

      set(state => ({
        sales: state.sales.map(sale =>
          sale.id === saleId ? updatedSale : sale
        )
      }));

      return updatedSale;
    } catch (error) {
      set({ error: error.response?.data?.error || 'Failed to update sale' });
      throw error;
    }
  },

  // Delete a sale
  deleteSale: async (saleId) => {
    try {
      await api.delete(`/api/crm/sales/${saleId}`);

      set(state => ({
        sales: state.sales.filter(sale => sale.id !== saleId)
      }));
    } catch (error) {
      set({ error: error.response?.data?.error || 'Failed to delete sale' });
      throw error;
    }
  },

  // ========== TRANSACTION ACTIONS ==========

  // Fetch transactions for a lead
  fetchLeadTransactions: async (leadId, type = null) => {
    set({ salesLoading: true });
    try {
      const params = type ? `?type=${type}` : '';
      const response = await api.get(`/api/crm/leads/${leadId}/transactions${params}`);
      set({ transactions: response.data, salesLoading: false });
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.error || 'Failed to fetch transactions', salesLoading: false });
      throw error;
    }
  },

  // Create a new transaction
  createTransaction: async (leadId, transactionData) => {
    set({ salesLoading: true });
    try {
      const response = await api.post(`/api/crm/leads/${leadId}/transactions`, transactionData);
      const newTransaction = response.data;

      set(state => ({
        transactions: [newTransaction, ...state.transactions],
        salesLoading: false
      }));

      return newTransaction;
    } catch (error) {
      set({ error: error.response?.data?.error || 'Failed to create transaction', salesLoading: false });
      throw error;
    }
  },

  // Update a transaction
  updateTransaction: async (transactionId, updates) => {
    try {
      const response = await api.put(`/api/crm/transactions/${transactionId}`, updates);
      const updatedTransaction = response.data;

      set(state => ({
        transactions: state.transactions.map(t =>
          t.id === transactionId ? updatedTransaction : t
        )
      }));

      return updatedTransaction;
    } catch (error) {
      set({ error: error.response?.data?.error || 'Failed to update transaction' });
      throw error;
    }
  },

  // Delete a transaction
  deleteTransaction: async (transactionId) => {
    try {
      await api.delete(`/api/crm/transactions/${transactionId}`);

      set(state => ({
        transactions: state.transactions.filter(t => t.id !== transactionId)
      }));
    } catch (error) {
      set({ error: error.response?.data?.error || 'Failed to delete transaction' });
      throw error;
    }
  },

  // ========== FINANCIAL SUMMARY ==========

  // Fetch financial summary for a lead
  fetchLeadFinancialSummary: async (leadId) => {
    try {
      const response = await api.get(`/api/crm/leads/${leadId}/financial-summary`);
      set({ financialSummary: response.data });
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.error || 'Failed to fetch financial summary' });
      throw error;
    }
  },

  // ========== SALES METRICS ==========

  // Fetch workspace sales metrics
  fetchSalesMetrics: async (dateFrom = null, dateTo = null) => {
    set({ salesLoading: true });
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);

      const response = await api.get(`/api/crm/sales/metrics?${params}`);
      set({ salesMetrics: response.data, salesLoading: false });
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.error || 'Failed to fetch sales metrics', salesLoading: false });
      throw error;
    }
  },

  // Fetch revenue evolution
  fetchRevenueEvolution: async (days = 30) => {
    try {
      const response = await api.get(`/api/crm/sales/revenue-evolution?days=${days}`);
      set({ revenueEvolution: response.data });
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.error || 'Failed to fetch revenue evolution' });
      throw error;
    }
  },

  // Clear sales data (when closing modal)
  clearSalesData: () => set({
    sales: [],
    transactions: [],
    financialSummary: null
  }),
}));

export default useCrmStore;
